/**
 * Hook for accessing retailer configuration and utilities
 */

import { useMemo } from "react";
import { useRetailer } from "./useRetailer";
import {
  getRetailerConfig,
  getUserStoresForRetailer,
  getUserCategoriesForRetailer,
  normalizeStoreId,
  formatStoreDisplay,
  getStoreIdField,
  getAddressField,
  getDisplayFields,
  // getCategoryServiceHook,
  getCategoriesEndpoint,
  getProductsEndpoint,
  getStoresEndpoint,
  getDefaultStoreKey,
  getProductsQueryKey,
  getCategoriesQueryKey,
  getStoresQueryKey,
  getStoreCacheKey,
  prepareUserUpdateData,
  getAvailableTabs,
  isRetailerFeatureEnabled,
  type RetailerConfig,
  type RetailerType,
} from "../config/retailers";
import type { User } from "../services/UserService";

export interface UseRetailerConfigReturn {
  // Current retailer info
  retailer: RetailerType;
  config: RetailerConfig;

  // User data helpers
  getUserStores: (user: User | null) => string[];
  getUserCategories: (user: User | null) => string[];

  // Store utilities
  normalizeStoreId: (storeId: string) => string;
  formatStoreDisplay: (store: any) => string;

  // Data fields
  storeIdField: string;
  addressField: string;
  displayFields: string[];

  // API endpoints
  categoriesEndpoint: string;
  productsEndpoint: string;
  storesEndpoint: string;

  // Query keys
  getProductsQueryKey: (
    storeId: string,
    category?: string,
    showNewestOnly?: boolean,
    showHotDeals?: boolean
  ) => string[];
  getCategoriesQueryKey: () => string[];
  getStoresQueryKey: () => string[];

  // Cache keys
  getStoreCacheKey: (
    storeId: string,
    category?: string,
    showNewestOnly?: boolean,
    showHotDeals?: boolean
  ) => string;

  // User update helpers
  prepareUserUpdate: (updateData: {
    categories?: string[];
    stores?: string[];
    searchTerms?: string[];
  }) => Partial<User>;

  // Feature availability
  isFeatureEnabled: (feature: keyof RetailerConfig["features"]) => boolean;
  availableTabs: Array<{
    id: string;
    name: string;
    shortName: string;
    enabled: boolean;
  }>;

  // Default store key
  defaultStoreKey: string;
}

export function useRetailerConfig(): UseRetailerConfigReturn {
  const retailer = useRetailer();

  return useMemo(() => {
    const config = getRetailerConfig(retailer);

    return {
      // Current retailer info
      retailer,
      config,

      // User data helpers
      getUserStores: (user: User | null) =>
        getUserStoresForRetailer(user, retailer),
      getUserCategories: (user: User | null) =>
        getUserCategoriesForRetailer(user, retailer),

      // Store utilities
      normalizeStoreId: (storeId: string) =>
        normalizeStoreId(storeId, retailer),
      formatStoreDisplay: (store: any) => formatStoreDisplay(store, retailer),

      // Data fields
      storeIdField: getStoreIdField(retailer),
      addressField: getAddressField(retailer),
      displayFields: getDisplayFields(retailer),

      // API endpoints
      categoriesEndpoint: getCategoriesEndpoint(retailer),
      productsEndpoint: getProductsEndpoint(retailer),
      storesEndpoint: getStoresEndpoint(retailer),

      // Query keys
      getProductsQueryKey: (
        storeId: string,
        category?: string,
        showNewestOnly?: boolean,
        showHotDeals?: boolean
      ) =>
        getProductsQueryKey(
          storeId,
          category,
          showNewestOnly,
          retailer,
          showHotDeals
        ),
      getCategoriesQueryKey: () => getCategoriesQueryKey(retailer),
      getStoresQueryKey: () => getStoresQueryKey(retailer),

      // Cache keys
      getStoreCacheKey: (
        storeId: string,
        category?: string,
        showNewestOnly?: boolean,
        showHotDeals?: boolean
      ) =>
        getStoreCacheKey(
          retailer,
          storeId,
          category,
          showNewestOnly,
          showHotDeals
        ),

      // User update helpers
      prepareUserUpdate: (updateData: {
        categories?: string[];
        stores?: string[];
        searchTerms?: string[];
      }) => prepareUserUpdateData(retailer, updateData),

      // Feature availability
      isFeatureEnabled: (feature: keyof RetailerConfig["features"]) =>
        isRetailerFeatureEnabled(retailer, feature),
      availableTabs: getAvailableTabs(retailer),

      // Default store key
      defaultStoreKey: getDefaultStoreKey(retailer),
    };
  }, [retailer]);
}
